import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './style.less';

export const CountdownTimer = ({ endDate }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const prevTimeRef = useRef(null);

  useEffect(() => {
    // Input validation for endDate
    if (!endDate) {
      console.error('CountdownTimer: endDate prop is required');
      setIsExpired(true);
      return;
    }

    const calculateTimeLeft = () => {
      const now = new Date().getTime();

      // Enhanced date parsing with validation
      let end;
      try {
        end = new Date(endDate);
        // Check if date is valid
        if (isNaN(end.getTime())) {
          console.error('CountdownTimer: Invalid date format provided:', endDate);
          setIsExpired(true);
          return null;
        }
        end = end.getTime();
      } catch (error) {
        console.error('CountdownTimer: Error parsing date:', error);
        setIsExpired(true);
        return null;
      }

      const difference = end - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft(null);
        return null;
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { hours, minutes, seconds };
    };

    // Initial calculation
    const initial = calculateTimeLeft();
    if (initial) {
      setTimeLeft(initial);
      prevTimeRef.current = initial;
      // Set urgency indicator if less than 1 hour
      setIsUrgent(initial.hours === 0);
    }

    // Update every second
    const timer = setInterval(() => {
      const time = calculateTimeLeft();

      // Performance optimization: Only update state if values actually changed
      if (time && prevTimeRef.current) {
        if (
          time.hours !== prevTimeRef.current.hours ||
          time.minutes !== prevTimeRef.current.minutes ||
          time.seconds !== prevTimeRef.current.seconds
        ) {
          setTimeLeft(time);
          prevTimeRef.current = time;
          // Update urgency state when crossing the 1-hour threshold
          setIsUrgent(time.hours === 0);
        }
      } else if (time) {
        setTimeLeft(time);
        prevTimeRef.current = time;
        setIsUrgent(time.hours === 0);
      }
    }, 1000);

    // Cleanup on unmount
    return () => clearInterval(timer);
  }, [endDate]);

  // Don't render if expired or no time data
  if (isExpired || !timeLeft) {
    return null;
  }

  // Check if less than 24 hours remaining
  const totalHours = timeLeft.hours;
  if (totalHours >= 24) {
    return null;
  }

  const fadeInVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
  };

  return (
    <motion.div
      className={`countdown-timer ${isUrgent ? 'countdown-timer--urgent' : ''}`}
      initial="hidden"
      animate="visible"
      variants={fadeInVariants}
      role="timer"
      aria-live="polite"
      aria-label={t('countdown.ariaLabel')}
      data-testid="countdown-timer"
    >
      <div className="countdown-icon" aria-hidden="true">
        <i className="fas fa-clock"></i>
      </div>
      <div className="countdown-content">
        <span className="countdown-label">{t('countdown.endsIn')}</span>
        <span className="countdown-time">
          <span className="time-unit">
            <span className="time-value">{String(timeLeft.hours).padStart(2, '0')}</span>
            <span className="time-text">{t('countdown.hours')}</span>
          </span>
          <span className="time-separator">:</span>
          <span className="time-unit">
            <span className="time-value">{String(timeLeft.minutes).padStart(2, '0')}</span>
            <span className="time-text">{t('countdown.minutes')}</span>
          </span>
          <span className="time-separator">:</span>
          <span className="time-unit">
            <span className="time-value">{String(timeLeft.seconds).padStart(2, '0')}</span>
            <span className="time-text">{t('countdown.seconds')}</span>
          </span>
        </span>
      </div>
    </motion.div>
  );
};
